<i18n>
{
  "de": {
    "activeFilters": "{count} Filter aktiv | {count} Filter aktiv",
    "resetAll": "Zurücksetzen",
    "topicFilter": "Thema: {topic}",
    "multipleTopics": "{count} Themen"
  },
  "en": {
    "activeFilters": "{count} filter active | {count} filters active",
    "resetAll": "Reset",
    "topicFilter": "Topic: {topic}",
    "multipleTopics": "{count} topics"
  }
}
</i18n>

<template>
  <transition name="slide-down">
    <div
      v-if="hasActiveFilters && showIndicator"
      class="active-filter-indicator"
    >
      <div class="indicator-content">
        <div class="filter-info">
          <i class="icon-filter" />
          <span class="filter-text">
            <!-- Show topic info if only topics are filtered -->
            <template v-if="hasOnlyTopicFilters">
              <span v-if="activeTopics.length === 1">{{ $t('topicFilter', { topic: activeTopics[0].name }) }}</span>
              <span v-else>{{ $t('multipleTopics', { count: activeTopics.length }) }}</span>
            </template>
            <!-- Show general filter count -->
            <template v-else>
              {{ $tc('activeFilters', totalActiveFilters, { count: totalActiveFilters }) }}
            </template>
          </span>
        </div>
        <button
          class="reset-btn"
          @click="resetAllFilters"
        >
          <i class="icon-times" />
          {{ $t('resetAll') }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ActiveFilterIndicator',
  data() {
    return {
      showIndicator: false
    }
  },
  computed: {
    hasActiveFilters() {
      return this.totalActiveFilters > 0 || this.hasActiveTopics
    },
    totalActiveFilters() {
      if (!this.postFilter) return 0
      return this.postFilter.product.length +
        this.postFilter.reward.length +
        this.postFilter.months.length +
        this.postFilter.contents.length +
        (this.postFilter.public ? 1 : 0) +
        (this.postFilter.limitToFollowers ? 1 : 0)
    },
    hasActiveTopics() {
      return this.postFilter && this.postFilter.tags && this.postFilter.tags.length > 0
    },
    hasOnlyTopicFilters() {
      return this.hasActiveTopics && this.totalActiveFilters === 0
    },
    activeTopics() {
      if (!this.hasActiveTopics) return []
      return this.postFilter.tags
    },
    shouldShow() {
      // Only show on mobile and on posts routes
      return this.isMobileView && this.$route && this.$route.name &&
             (this.$route.name.startsWith('id-posts') || this.$route.name.startsWith('id___'))
    },
    ...mapState(['postFilter'])
  },
  watch: {
    hasActiveFilters: {
      handler(newVal) {
        if (this.shouldShow) {
          this.showIndicator = newVal
        } else {
          this.showIndicator = false
        }
        this.$emit('visibility-changed', this.showIndicator)
      },
      immediate: true
    },
    shouldShow: {
      handler(newVal) {
        if (!newVal) {
          this.showIndicator = false
        } else {
          this.showIndicator = this.hasActiveFilters
        }
        this.$emit('visibility-changed', this.showIndicator)
      },
      immediate: true
    }
  },
  methods: {
    resetAllFilters() {
      this.$root.$emit('resetAllFilters')
      // Navigate to clean posts URL
      this.$router
        .push(this.localePath({ name: 'id-posts', params: { id: this.$route.params.id } }))
        .catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.warn('Navigation error:', err)
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/sass/_variables';

.active-filter-indicator {
  position: sticky;
  top: 52px; // Below main nav
  z-index: 2;
  padding: 0;
  margin: 0;
  // Fallback colors using SCSS variable
  background: rgba($primary, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba($primary, 0.3);
}

.indicator-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 15px;
  max-width: 100%;
}

.filter-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0; // Allow text truncation

  i {
    color: white;
    font-size: 14px;
    flex-shrink: 0;
  }
}

.filter-text {
  color: white;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reset-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 15px;
  color: white;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;

  &:hover, &:active {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-1px);
  }

  i {
    font-size: 10px;
  }
}

// Transitions
.slide-down-enter-active, .slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter, .slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

// Ensure it appears above content
@media only screen and (max-width: $mobile) {
  .active-filter-indicator {
    position: sticky;
    top: 0;
    margin-bottom: -1px; // Prevent spacing issues
  }
}
</style>
